import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  UPDATES_BANNER_REPEAT,
  updatesBannerDismissKey,
  updatesBannerDurationSeconds,
  updatesBannerEntries,
} from '../../../content/updatesBanner';

function readDismissed(key) {
  try {
    return window.localStorage.getItem(key) === 'dismissed';
  } catch {
    return false;
  }
}

function focusAfterDismiss() {
  const logo = document.querySelector('img[data-brand-logo]')?.closest('a');
  const main = document.querySelector('main');
  const target = logo || main;
  if (!target) return;
  if (target === main && !main.hasAttribute('tabindex')) {
    main.setAttribute('tabindex', '-1');
  }
  target.focus();
}

function BannerLink({ entry, hidden = false }) {
  return (
    <a
      href={entry.href}
      tabIndex={hidden ? -1 : undefined}
      aria-hidden={hidden ? true : undefined}
      className="updates-banner-copy inline-flex min-h-[44px] shrink-0 items-center whitespace-nowrap text-sm font-bold leading-none text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
    >
      <span className="mr-3 text-brand-500" aria-hidden="true">
        ●
      </span>
      {entry.copy}
    </a>
  );
}

function BannerSequence({ entries, hidden = false, idPrefix }) {
  return entries.map((entry, index) => (
    <BannerLink
      key={`${idPrefix}-${entry.href}-${index}`}
      entry={entry}
      hidden={hidden}
    />
  ));
}

export default function UpdatesBanner() {
  const entries = updatesBannerEntries();
  const key = updatesBannerDismissKey(entries);
  const [visible, setVisible] = useState(() => entries.length > 0 && !readDismissed(key));

  if (!visible || entries.length === 0) return null;

  const duration = `${updatesBannerDurationSeconds(entries)}s`;

  return (
    <div
      role="region"
      aria-label="Site announcement"
      data-ticker-entries={entries.length}
      className="updates-banner relative z-40 flex h-12 items-center bg-brand-900 text-white"
    >
      <div className="updates-banner-viewport flex h-full min-w-0 flex-1 items-center overflow-hidden">
        <div
          className="updates-banner-track flex w-max items-center"
          style={{ '--updates-banner-duration': duration }}
        >
          <div className="updates-banner-group flex items-center">
            <BannerSequence entries={entries} idPrefix="a" />
            {Array.from({ length: UPDATES_BANNER_REPEAT - 1 }, (_, index) => (
              <BannerSequence
                key={`fill-${index}`}
                entries={entries}
                hidden
                idPrefix={`fill-${index}`}
              />
            ))}
          </div>
          <div className="updates-banner-group flex items-center" aria-hidden="true">
            {Array.from({ length: UPDATES_BANNER_REPEAT }, (_, index) => (
              <BannerSequence
                key={`loop-${index}`}
                entries={entries}
                hidden
                idPrefix={`loop-${index}`}
              />
            ))}
          </div>
        </div>
        <div
          className="updates-banner-static w-full items-center px-4"
          tabIndex={0}
          role="region"
          aria-label="Site announcements"
        >
          <BannerSequence entries={entries} idPrefix="static" />
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          try {
            window.localStorage.setItem(key, 'dismissed');
          } catch {
            /* private mode */
          }
          focusAfterDismiss();
          setVisible(false);
        }}
        aria-label="Dismiss announcement"
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
      >
        <XMarkIcon aria-hidden="true" className="h-5 w-5 text-brand-500" />
      </button>
    </div>
  );
}
